import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Switch } from '@mui/material'
import { useDealId } from 'pages/DecisionEditPage/hooks/useDealId'
import {
  CurrencyConvertLineItem,
  useCurrencyConvertEditPage,
} from 'queries/useCurrencyConvertEditPage'
import { useStyles } from '../../styles/ToggleComponent.styles'

interface CurrencyToggleProps {
  dealCurrency?: string
  checked?: boolean
  fetchOnMount?: boolean
  onCurrencyDataChange?: (
    plData: CurrencyConvertLineItem[] | null,
    pnData: CurrencyConvertLineItem[] | null,
    isUSD: boolean
  ) => void
  onLoadingChange?: (isLoading: boolean) => void
}

export const CurrencyToggle = ({
  dealCurrency = 'EUR',
  checked,
  fetchOnMount = false,
  onCurrencyDataChange,
  onLoadingChange,
}: CurrencyToggleProps) => {
  const { classes } = useStyles()
  const [isUSD, setIsUSD] = useState(checked ?? false)
  const dealId = useDealId()
  const isUSDRef = useRef(isUSD)
  const requestIdRef = useRef(0)

  const { getCurrencyConversion } = useCurrencyConvertEditPage()

  useEffect(() => {
    if (checked !== undefined) {
      setIsUSD(checked)
      isUSDRef.current = checked
    }
  }, [checked])

  const fetchCurrencyData = useCallback(
    async (newIsUSD: boolean) => {
      if (!dealId || !newIsUSD) return

      const currentRequestId = ++requestIdRef.current

      onLoadingChange?.(true)
      try {
        const [plResult, pnResult] = await Promise.all([
          getCurrencyConversion({
            variables: { dealId, lineTypeCode: 'PL', toCurrencyCode: 'UD' },
          }),
          getCurrencyConversion({
            variables: { dealId, lineTypeCode: 'PN', toCurrencyCode: 'UD' },
          }),
        ])

        if (currentRequestId !== requestIdRef.current || !isUSDRef.current) return

        onCurrencyDataChange?.(
          plResult.data?.currencyConvertDecisionEditPage ?? null,
          pnResult.data?.currencyConvertDecisionEditPage ?? null,
          true
        )
      } catch (error) {
        if (currentRequestId !== requestIdRef.current || !isUSDRef.current) return
        console.error('Error fetching currency conversion:', error)
      } finally {
        if (currentRequestId === requestIdRef.current) {
          onLoadingChange?.(false)
        }
      }
    },
    [dealId, getCurrencyConversion, onCurrencyDataChange, onLoadingChange]
  )

  useEffect(() => {
    if (fetchOnMount) {
      isUSDRef.current = true
      setIsUSD(true)
      fetchCurrencyData(true)
    }
  }, [])

  const handleToggleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked

      isUSDRef.current = isChecked
      setIsUSD(isChecked)

      if (isChecked) {
        await fetchCurrencyData(true)
      } else {
        requestIdRef.current++
        onCurrencyDataChange?.(null, null, false)
        onLoadingChange?.(false)
      }
    },
    [fetchCurrencyData, onCurrencyDataChange, onLoadingChange]
  )

  return (
    <Box className={classes.toggleContainer}>
      <span className={classes.label}>{dealCurrency}</span>
      <Switch
        checked={isUSD}
        onChange={handleToggleChange}
        className={classes.switch}
        slotProps={{ input: { 'aria-label': `toggle between ${dealCurrency} and USD view` } }}
      />
      <span className={classes.label}>USD</span>
    </Box>
  )
}
